import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Space, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  useSupabaseSession,
  parseUserRole,
  isAdminRole,
  isFleetOwnerRole,
  useFleetOperator,
  usePortalPermissions,
  isJobWritableForFleet,
} from '@ubus/shared';
import AdminSectionPage from '../components/AdminSectionPage';
import JobForm from '../components/JobForm';
import type { JobFormRow } from '../components/JobForm';
import ForbiddenPage from './ForbiddenPage';

function parseDateParam(raw: string | null): Dayjs | null {
  if (raw && dayjs(raw, 'YYYY-MM-DD', true).isValid()) {
    return dayjs(raw, 'YYYY-MM-DD');
  }
  return null;
}

function JobFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isCreate = !id;

  const { hasPermission, loading: permLoading } = usePortalPermissions();
  const canCreate = hasPermission('jobs.create');
  const canEdit = hasPermission('jobs.edit');
  const { session, loading: authLoading } = useSupabaseSession();
  const user = session?.user ?? null;
  const isHqAdmin = isAdminRole(parseUserRole(user));
  const isFleetOwner = isFleetOwnerRole(parseUserRole(user));
  const { operatorId, loading: fleetLoading } = useFleetOperator();
  const canAccessPage = isHqAdmin || (isFleetOwner && Boolean(operatorId));
  const lockedFleetOperatorId = isHqAdmin ? null : operatorId;
  const gatesLoading = authLoading || fleetLoading || permLoading;

  const [loading, setLoading] = useState(!isCreate);
  const [editingJob, setEditingJob] = useState<JobFormRow | null>(null);
  const [scheduleDate, setScheduleDate] = useState<Dayjs>(() => {
    return parseDateParam(searchParams.get('date')) ?? dayjs().startOf('day');
  });

  const isJobWritable = useCallback(
    (job: JobFormRow) => {
      if (!canEdit) return false;
      if (isHqAdmin) return true;
      return isJobWritableForFleet(job, lockedFleetOperatorId);
    },
    [canEdit, isHqAdmin, lockedFleetOperatorId]
  );

  useEffect(() => {
    if (gatesLoading || !canAccessPage) return;
    if (isCreate) {
      setScheduleDate(parseDateParam(searchParams.get('date')) ?? dayjs().startOf('day'));
      setEditingJob(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        let query = supabase.from('jobs').select('*, job_legs(*)').eq('id', id);
        if (lockedFleetOperatorId) {
          query = query.or(
            `fleet_operator_id.eq.${lockedFleetOperatorId},assist_from_fleet_operator_id.eq.${lockedFleetOperatorId}`
          );
        }
        const { data, error } = await query.maybeSingle();
        if (cancelled) return;
        if (error) throw error;
        if (!data) {
          message.error(t('jobs.notFound'));
          navigate('/jobs', { replace: true });
          return;
        }

        const job = data as JobFormRow;
        job.job_legs = (job.job_legs ?? []).sort((a, b) => a.sequence - b.sequence);

        if (!isJobWritable(job)) {
          message.warning(t('jobs.notWritable'));
          navigate(`/jobs/${id}`, { replace: true });
          return;
        }

        setEditingJob(job);
        setScheduleDate(dayjs(job.schedule_date, 'YYYY-MM-DD'));
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        message.error(err instanceof Error ? err.message : String(err));
        navigate('/jobs', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    gatesLoading,
    canAccessPage,
    isCreate,
    id,
    lockedFleetOperatorId,
    searchParams,
    isJobWritable,
    navigate,
    t,
  ]);

  if (gatesLoading) {
    return (
      <AdminSectionPage
        title={isCreate ? t('jobs.newPageTitle') : t('jobs.editPageTitle')}
        loading
      />
    );
  }

  if (!canAccessPage) {
    return <ForbiddenPage />;
  }

  if (isCreate && !canCreate) {
    return <ForbiddenPage />;
  }

  return (
    <AdminSectionPage
      title={isCreate ? t('jobs.newPageTitle') : t('jobs.editPageTitle')}
      loading={loading}
      extra={
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/jobs')}>
          {t('jobs.backToList')}
        </Button>
      }
    >
      {!loading ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <JobForm
            scheduleDate={scheduleDate}
            editingJob={editingJob}
            lockedFleetOperatorId={lockedFleetOperatorId}
            onCancel={() => navigate('/jobs')}
            onSaved={() => {
              message.success(editingJob ? t('jobs.updated') : t('jobs.created'));
              navigate('/jobs');
            }}
          />
        </Space>
      ) : null}
    </AdminSectionPage>
  );
}

export default JobFormPage;
